import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, TextField, Button, IconButton, Switch, Stack, Chip, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

interface EndpointRow {
  id: number;
  base_url: string;
  label?: string | null;
  enabled: boolean;
  // Health/metrics (optional)
  online?: boolean | null;
  last_checked_at?: string | null;
  last_healthy_at?: string | null;
  success_count?: number | null;
  error_count?: number | null;
  avg_latency_ms?: number | null;
  last_error?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

const OllamaEndpointsAdmin: React.FC = () => {
  const { keycloak } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<EndpointRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const load = async (doRefresh: boolean = false) => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      const resp = await apiService.getOllamaEndpoints({ refresh: doRefresh });
      const data = resp?.data;
      const arr = Array.isArray(data)
        ? data
        : (Array.isArray(data?.endpoints) ? data.endpoints
          : (Array.isArray(data?.items) ? data.items
            : (data && typeof data === 'object' && Array.isArray((data as any).data) ? (data as any).data : [])));
      setRows(arr as any[]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load endpoints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load without forcing inline health probes to keep API responsive
    load(false);
    // Light auto-refresh every 30s without forcing probes; manual button triggers probes
    const interval = setInterval(() => load(false), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async () => {
    const base_url = newUrl.trim().replace(/\/$/, '');
    if (!base_url) return;
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      await apiService.addOllamaEndpoint({ base_url, label: newLabel || undefined, enabled: true });
      setNewUrl('');
      setNewLabel('');
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to add endpoint');
    }
  };

  const handleToggle = async (row: EndpointRow) => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      await apiService.updateOllamaEndpoint(row.id, { enabled: !row.enabled });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to update endpoint');
    }
  };

  const handleDelete = async (row: EndpointRow) => {
    const ok = window.confirm(`Delete endpoint ${row.base_url}?`);
    if (!ok) return;
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      await apiService.deleteOllamaEndpoint(row.id);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to delete endpoint');
    }
  };

  const handleInlineLabelChange = async (row: EndpointRow, label: string) => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      await apiService.updateOllamaEndpoint(row.id, { label });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to update label');
    }
  };

  const handleInlineUrlChange = async (row: EndpointRow, url: string) => {
    const base_url = (url || '').trim().replace(/\/$/, '');
    if (!base_url || base_url === row.base_url) return;
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      await apiService.updateOllamaEndpoint(row.id, { base_url });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to update base URL');
    }
  };

  if (loading) return <LoadingSpinner message="Loading Ollama endpoints..." />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Ollama Endpoints</Typography>
      {error && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Add Endpoint</Typography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth label="Base URL (e.g., http://localhost:11434)" value={newUrl} onChange={e => setNewUrl(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Label (optional)" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="contained" onClick={handleAdd}>Add</Button>
          </Grid>
        </Grid>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Changes take effect within ~30 seconds automatically; no restart required.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Configured Endpoints</Typography>
          <Button variant="outlined" size="small" onClick={() => load(true)}>Refresh</Button>
        </Stack>
        {rows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No endpoints configured yet.</Typography>
        ) : (
          <Box>
            {rows.map((r) => {
              const online = (r.online === true) || (r.online as any) === 1;
              const avgMs = (typeof r.avg_latency_ms === 'number' && r.avg_latency_ms! >= 0) ? r.avg_latency_ms : null;
              const lastChecked = r.last_checked_at ? new Date(r.last_checked_at).toLocaleString() : null;
              const lastHealthy = r.last_healthy_at ? new Date(r.last_healthy_at).toLocaleString() : null;
              const succ = (typeof r.success_count === 'number') ? r.success_count : null;
              const errs = (typeof r.error_count === 'number') ? r.error_count : null;
              return (
                <Box key={r.id} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Base URL"
                        defaultValue={r.base_url}
                        onBlur={(e) => handleInlineUrlChange(r, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Label"
                        value={r.label || ''}
                        onChange={(e) => handleInlineLabelChange(r, e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">Enabled</Typography>
                        <Switch checked={!!r.enabled} onChange={() => handleToggle(r)} />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton color="error" onClick={() => handleDelete(r)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={online ? 'Online' : (r.online === null || r.online === undefined ? 'Unknown' : 'Offline')} color={online ? 'success' : (r.online === null || r.online === undefined ? 'default' : 'error')} />
                        <Chip size="small" label={`Avg: ${avgMs !== null ? avgMs : '—'} ms`} variant="outlined" />
                        <Chip size="small" label={`Success: ${succ !== null ? succ : '—'}`} color="success" variant="outlined" />
                        <Chip size="small" label={`Errors: ${errs !== null ? errs : '—'}`} color={(errs && errs > 0) ? 'error' : 'default'} variant="outlined" />
                        <Chip size="small" label={`Last Checked: ${lastChecked || '—'}`} variant="outlined" />
                        <Chip size="small" label={`Last Healthy: ${lastHealthy || '—'}`} variant="outlined" />
                        {r.last_error && (
                          <Tooltip title={r.last_error} placement="top" arrow>
                            <Chip size="small" label={`Last Error`} color="error" variant="filled" />
                          </Tooltip>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OllamaEndpointsAdmin;
